import { apiIsPasswordBlacklisted } from './apiIsPasswordBlacklisted.ts';
import { API_ERROR_CODES, apiGetOneTimeToken, ONBOARDING_API_BASE_URL } from './index.ts';

/**
 * Set password for user
 */

type ApiSetPasswordResult = {
  data?: {
    requiresOtp: boolean;
    oneTimeToken?: string;
    p_id?: string;
  };
  errorCode?: API_ERROR_CODES;
  errorMessage?: string;
};

export const apiSetPassword = async (
  password: string,
  csrfToken: string,
  jid: string | null
): Promise<ApiSetPasswordResult> => {
  // first check if the password is blacklisted
  const { data, errorCode, errorMessage } = await apiIsPasswordBlacklisted(password);
  if (data?.passwordBlacklisted) {
    return Promise.resolve({
      errorCode: API_ERROR_CODES.PASSWORD_BLACKLISTED,
      errorMessage,
    });
  } else if (errorCode) {
    return Promise.resolve({
      errorCode,
      errorMessage,
    });
  }

  return fetch(`${ONBOARDING_API_BASE_URL}/auth/set-password`, {
    method: 'POST',
    body: JSON.stringify({ password, jid }),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        let errorMessage = await response.text();
        let errorCode = API_ERROR_CODES.GENERIC;
        try {
          const errorJson = JSON.parse(errorMessage);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          // Ignore JSON parse errors, use raw errorMessage
        }

        if (errorMessage.includes('[PasswordBlacklisted]')) {
          errorCode = API_ERROR_CODES.PASSWORD_BLACKLISTED;
        } else if (response.status === 401 || response.status === 502 || response.status === 500) {
          errorCode = API_ERROR_CODES.GENERIC;
        }
        return {
          errorCode,
          errorMessage,
        };
      }

      const responseJson = await response.json();

      // if they don't need to do OTP we can fetch the one time token straight away
      let oneTimeToken = undefined;
      if (!responseJson.requiresOtp) {
        const { data: oneTimeTokenData, errorCode, errorMessage } = await apiGetOneTimeToken(csrfToken);
        if (oneTimeTokenData) {
          oneTimeToken = oneTimeTokenData.oneTimeToken;
        } else if (errorCode) {
          return {
            errorCode,
            errorMessage,
          };
        }
      }

      return {
        data: {
          ...responseJson,
          oneTimeToken,
        },
      } as ApiSetPasswordResult;
    })
    .catch((err) => ({
      errorCode: API_ERROR_CODES.GENERIC,
      errorMessage: err?.message || 'Network error. Please try again.',
    }));
};
